import { Box, createStyles, Divider, Grid, makeStyles } from '@material-ui/core'
import React from 'react'
import {
  DateField,
  EditButton,
  FunctionField,
  ReferenceField,
  sanitizeFetchType,
  Show,
  ShowProps,
  SimpleShowLayout,
  Tab,
  TabbedShowLayout,
  TextField,
  TitleProps,
  useQuery,
  useTranslate,
} from 'react-admin'
import { Authority } from '../../../core/auth/Authority'
import { PassageDto } from '../../../core/dto/device/passages/passage.dto'
import { HardwareVersions } from '../../../core/enum/HardwareVersions'
import { ResourceName } from '../../../core/ResourceName'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { DeviceStatusesTab } from '../common/modules/ModuleStatus'
import { PassageForceReportStateButton } from './passage-show/PassageForceReportStateButton'
import { PassageResetCountersButton } from './passage-show/PassageResetCountersButton'
import { PassageSetFreePassBackwardButton } from './passage-show/PassageSetFreePassBackwardButton'
import { PassageSetFreePassForwardButton } from './passage-show/PassageSetFreePassForwardButton'
import { PassageSetLockButton } from './passage-show/PassageSetLockButton'
import { PassageSetPowerButton } from './passage-show/PassageSetPowerButton'
import { PassageSetPPOZButton } from './passage-show/PassageSetPPOZButton'
import { PassageResetModulesButton } from './passage-show/PassageResetModulesButton'
import { PassageSetReverseDirectionButton } from './passage-show/PassageSetReverseDirectionButton'
import { CameraDto } from '../../../core/dto/device/camera/cameras/camera.dto'
import { DeviceCategoryExtendedDto } from '../../../core/dto/device-category/device-category-extended.dto'
import { SPOM4FullAddressField } from './SPOM4Address'
import { DeviceTypes } from '../../../core/enum/DeviceTypes'
import { PassageSetOpenButton } from './passage-show/PassageSetOpenButton'
import { PassageDirections } from '../../../core/enum/PassageDirections'
import { ButtonWithRecurringJob } from '../../common/ButtonWithRecurringJob'
import { BlankActions } from '../../common/BlankActions'

const useStyles = makeStyles(() =>
  createStyles({
    generalActions: {
      margin: '2px',
    },
  }),
)

interface PassageTitleProps extends TitleProps {
  record?: PassageDto
}

const ShowTitle = (data: PassageTitleProps) => {
  const { record: passage } = data
  const translate = useTranslate()
  return (
    <span>
      {translate('resources.passages.titles.show')}: {passage?.id}.{' '}
      {passage?.name}
    </span>
  )
}

const GeneralActions = ({ ...props }) => {
  const { record: passage } = props
  const hasAuthority = useHasAuthority()
  const classes = useStyles()

  return (
    <>
      <Divider />
      <Box p={1} display="block" textAlign="right">
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item>
            <EditButton
              className={classes.generalActions}
              basePath="/passages"
              record={passage}
              variant="contained"
              disabled={!hasAuthority(Authority.EDIT_PASSAGES)}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

const CountersActions = () => (
  <>
    <Divider />
    <Box p={1} display="block" textAlign="right">
      <Grid container direction="row" justify="flex-end" alignItems="center">
        <Grid item style={{ margin: '2px' }}>
          <ButtonWithRecurringJob
            mainAction={<PassageResetCountersButton />}
            recurringAction={<PassageResetCountersButton asRecurringJob />}
          />
        </Grid>
      </Grid>
    </Box>
  </>
)

const CountersTab = ({ ...props }) => (
  <SimpleShowLayout {...props}>
    <TextField source="freeEntrancesCount" />
    <TextField source="freeExitsCount" />
    <TextField source="ticketEntrancesCount" />
    <TextField source="ticketExitsCount" />
    <TextField source="unauthorizedEntrancesCount" />
    <TextField source="unauthorizedExitsCount" />
    <CountersActions />
  </SimpleShowLayout>
)

const SetStateActions = ({ ...props }) => {
  const { disabled, record: passage } = props

  return (
    <>
      <Box p={1} display="block" textAlign="right">
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              mainAction={
                <PassageSetFreePassBackwardButton disabled={disabled} />
              }
              recurringAction={
                <PassageSetFreePassBackwardButton
                  asRecurringJob
                  disabled={disabled}
                />
              }
            />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              mainAction={
                <PassageSetFreePassForwardButton disabled={disabled} />
              }
              recurringAction={
                <PassageSetFreePassForwardButton
                  asRecurringJob
                  disabled={disabled}
                />
              }
            />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              mainAction={<PassageSetPowerButton disabled={disabled} />}
              recurringAction={
                <PassageSetPowerButton asRecurringJob disabled={disabled} />
              }
            />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              mainAction={<PassageSetPPOZButton disabled={disabled} />}
              recurringAction={
                <PassageSetPPOZButton asRecurringJob disabled={disabled} />
              }
            />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              mainAction={
                <PassageSetOpenButton
                  direction={PassageDirections.IN}
                  disabled={disabled}
                />
              }
              recurringAction={
                <PassageSetOpenButton
                  asRecurringJob
                  direction={PassageDirections.IN}
                  disabled={disabled}
                />
              }
            />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              mainAction={
                <PassageSetOpenButton
                  direction={PassageDirections.OUT}
                  disabled={disabled}
                />
              }
              recurringAction={
                <PassageSetOpenButton
                  asRecurringJob
                  direction={PassageDirections.OUT}
                  disabled={disabled}
                />
              }
            />
          </Grid>
          {passage?.hardwareVersion !== HardwareVersions.SPOM4 && (
            <Grid item style={{ margin: '2px' }}>
              <ButtonWithRecurringJob
                mainAction={<PassageSetLockButton disabled={disabled} />}
                recurringAction={
                  <PassageSetLockButton asRecurringJob disabled={disabled} />
                }
              />
            </Grid>
          )}
          {passage?.hardwareVersion !== HardwareVersions.SPOM4 && (
            <Grid item style={{ margin: '2px' }}>
              <ButtonWithRecurringJob
                mainAction={
                  <PassageSetReverseDirectionButton disabled={disabled} />
                }
                recurringAction={
                  <PassageSetReverseDirectionButton
                    asRecurringJob
                    disabled={disabled}
                  />
                }
              />
            </Grid>
          )}
          {passage?.hardwareVersion !== HardwareVersions.SPOM4 && (
            <Grid item>
              <PassageForceReportStateButton />
            </Grid>
          )}
          {passage?.hardwareVersion !== HardwareVersions.SPOM4 && (
            <Grid item>
              <PassageResetModulesButton />
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  )
}

export const PassageTabs = ({ ...props }) => {
  const { simpleView, createdAt, ...rest } = props
  const { record: passage } = rest
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()

  const { data: serverDate } = useQuery(
    {
      type: sanitizeFetchType('getServerDateTime'),
      resource: ResourceName.CURRENT_USER,
      payload: {},
    },
    {
      enabled: !simpleView && !createdAt,
    },
  )

  return (
    <TabbedShowLayout {...rest} syncWithLocation={!simpleView}>
      <Tab label="resources.passages.tabs.general">
        <TextField source="id" label="ID" />
        <TextField source="name" />
        <FunctionField<PassageDto>
          source="isWatched"
          render={(record?: PassageDto) => (
            <div>
              {record?.isWatched
                ? translate('const.yes')
                : translate('const.no')}
            </div>
          )}
        />
        <DateField
          source="lastRefresh"
          showTime
          options={{
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }}
        />
        {passage?.categoryId && hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
          <ReferenceField
            source="categoryId"
            reference={ResourceName.DEVICE_CATEGORIES}
            link="show"
          >
            <FunctionField<DeviceCategoryExtendedDto>
              source="hierarchyString"
              render={(record?: DeviceCategoryExtendedDto) =>
                `${record?.id}. ${record?.hierarchyString}`
              }
            />
          </ReferenceField>
        )}
        {passage?.cameraId && hasAuthority(Authority.VIEW_CAMERAS) && (
          <ReferenceField
            source="cameraId"
            reference={ResourceName.CAMERAS}
            link="show"
          >
            <FunctionField<CameraDto>
              source="name"
              render={(camera?: CameraDto) =>
                `${camera?.id}. ${camera?.description}`
              }
            />
          </ReferenceField>
        )}
        {passage?.hardwareVersion === HardwareVersions.SPOM4 && (
          <SPOM4FullAddressField source="tsCommAddress" />
        )}
        <FunctionField<PassageDto>
          source="hardwareVersion"
          render={(record?: PassageDto) => (
            <div>
              {translate(
                `resources.enums.hardwareVersions.${record?.hardwareVersion}`,
              )}
            </div>
          )}
        />
        {passage?.tsCanId && hasAuthority(Authority.VIEW_TS_CANS) && (
          <ReferenceField
            source="tsCanId"
            reference={ResourceName.TS_CANS}
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
        )}
        {(!simpleView && <GeneralActions record={passage} />) || (
          <BlankActions />
        )}
      </Tab>
      {!simpleView && hasAuthority(Authority.VIEW_STATS_PASSAGE_COUNTERS) && (
        <Tab label="resources.passages.tabs.counters" path="counters">
          <CountersTab />
        </Tab>
      )}
      {passage?.jsonStatus && (
        <Tab label="resources.passages.tabs.statuses" path="statuses">
          <DeviceStatusesTab
            jsonStatus={passage.jsonStatus}
            reversedDirection={passage?.reversedDirection}
            deviceType={DeviceTypes.PASSAGE}
            referenceDateTime={createdAt || serverDate?.serverDateTime}
          />
          {(!simpleView && <SetStateActions />) || <BlankActions />}
        </Tab>
      )}
    </TabbedShowLayout>
  )
}

export const PassageShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <PassageTabs />
  </Show>
)
